import { isEmptyString, isNil, isNotNil } from '~/utilities/type-guards';
import type { TieredPlanSkuEnum } from '~/graphql';
import { merge } from 'lodash';
import type { UseTieredPlansDataQuery } from './__gql/useTieredPlans.gql.gen';
import { useUseTieredPlansDataQuery } from './__gql/useTieredPlans.gql.gen';
import type { DefaultPlanKeys, PlanLevels, TieredPlan } from './tieredPlans';
import { TIERED_PLANS, getDefaultPlansFilter } from './tieredPlans';

type PlanMap = Record<DefaultPlanKeys, TieredPlan>;

const TIERED_PLANS_BY_SKU: Record<string, TieredPlan> = {
  /* eslint-disable camelcase */
  free_2022_tiered: TIERED_PLANS.Free,
  plus_2022_tiered: TIERED_PLANS.Plus,
  plus_2024_tiered: TIERED_PLANS.Plus2024,
  pro_2022_tiered: TIERED_PLANS.Pro,
  advanced_2022_tiered: TIERED_PLANS.Advanced,
  advanced_2024_tiered_2tb: TIERED_PLANS.Advanced2TB,
  /* eslint-enable camelcase */
};

const DEFAULT_PLANS_BY_TIER_MAP = {
  Free: TIERED_PLANS.Free,
  Starter: TIERED_PLANS.Starter,
  Plus: TIERED_PLANS.Plus2024,
  Pro: TIERED_PLANS.Pro,
  Advanced: TIERED_PLANS.Advanced,
  Premium: TIERED_PLANS.Premium,
};
const DEFAULT_PLANS_BY_TIER = Object.values(DEFAULT_PLANS_BY_TIER_MAP);

const PRICING_EXPERIMENTS: {
  key: string;
  match: (params: { sku: TieredPlanSkuEnum; group: string }) => boolean;
}[] = [
  {
    key: 'list_price_increase2025',
    match: (params) => params.group === 'test' && params.sku.includes('2025_tiered'),
  },
  {
    key: 'starter_august2025',
    match: ({ group, sku }) => group === 'test' && sku.endsWith('_2025_august_tiered'),
  },
];
const PRICING_EXPERIMENT_KEYS = PRICING_EXPERIMENTS.map((experiment) => experiment.key);

type PurchaseableTieredPlan = TieredPlan & { purchasePlanType: string };

const findPreviousPlan = (planMap: PlanMap, plan: TieredPlan): TieredPlan | null => {
  if (plan.tier === 'starter' || plan.tier === 'plus') {
    return planMap.Free;
  }

  if (plan.tier === 'pro') {
    return planMap.Plus;
  }

  if (plan.tier === 'advanced') {
    return planMap.Pro;
  }

  if (plan.tier === 'premium') {
    return planMap.Advanced;
  }

  return null;
};

export const findNextPurchaseablePlan = (
  plan: TieredPlan,
  planMap: PlanMap,
): PurchaseableTieredPlan | null => {
  if (plan.tier === 'free') {
    return planMap.Plus as PurchaseableTieredPlan;
  }

  if (plan.tier === 'starter' || plan.tier === 'plus') {
    return planMap.Pro as PurchaseableTieredPlan;
  }

  if (plan.tier === 'pro') {
    return planMap.Advanced as PurchaseableTieredPlan;
  }

  return null;
};

const getStaticPlanBySKU = (sku?: string | null): TieredPlan | undefined => {
  if (isEmptyString(sku) || isNil(sku)) {
    return undefined;
  }

  return Object.prototype.hasOwnProperty.call(TIERED_PLANS_BY_SKU, sku) === true
    ? TIERED_PLANS_BY_SKU[sku]
    : undefined;
};

const getDynamicPlanBySKU = ({
  sku,
  plans,
}: {
  sku?: string | null;
  plans: TieredPlan[];
}): TieredPlan | undefined => {
  const staticPlan = getStaticPlanBySKU(sku);
  if (isNotNil(staticPlan)) {
    return staticPlan;
  }

  return plans.find((populatedPlan) => populatedPlan.sku === sku) ?? undefined;
};

const buildDynamicPlans = (plans: UseTieredPlansDataQuery['plans']): TieredPlan[] => {
  return plans
    .map((mappedPlan) => {
      const staticPlan = getStaticPlanBySKU(mappedPlan.sku);
      if (isNotNil(staticPlan) || mappedPlan.sku == null) {
        return null;
      }

      const {
        discontinuedOrNonSelfServeTiered,
        annualPricePerMonth: annual,
        archivedMediaLimit: archivedMedia,
        channelLimit: channels,
        fixedMediaOveragePrice: mediaOveragePrice,
        flexibleMediaLimit: includedMedia,
        price: monthly,
        sku,
        userLimit: users,
        inflexibleMediaLimit: totalMedia = 0,
      } = mappedPlan;
      const basePlan = DEFAULT_PLANS_BY_TIER.find(
        (plan) =>
          isNotNil(plan.sku) && isNotNil(sku) && plan.sku.split('_')[0] === sku.split('_')[0],
      );

      const defaultHighlights = basePlan?.highlights.default.map((highlightCopy) => {
        let copy = highlightCopy;
        const regex = /(Up to )(\b\d[\d,.]+\b)( videos)/;
        if (regex.test(copy)) {
          copy = copy.replace(regex, `$1${totalMedia}$3`);
        }
        return copy;
      });

      return merge({}, basePlan, {
        highlights: {
          default: defaultHighlights,
        },
        sku: sku as TieredPlanSkuEnum,
        data: {
          isDiscontinuedOrNonSelfServeTiered: discontinuedOrNonSelfServeTiered,
          mediaOveragePrice,
          limits: {
            includedMedia: includedMedia ?? undefined,
            channels: channels ?? 0,
            users: users ?? 0,
            totalMedia: totalMedia ?? 0,
            archivedMedia: archivedMedia ?? 0,
          },
          price: { annual, monthly },
        } satisfies TieredPlan['data'],
      }) satisfies TieredPlan;
    })
    .filter((plan) => plan != null) as TieredPlan[];
};

interface UseTieredPlansParams {
  unfiltered?: boolean;
  planFilter?: ReturnType<typeof getDefaultPlansFilter> | null | undefined;
  currentPlan?: TieredPlan;
  minimumPlanLevel?: PlanLevels;
}

type UseTieredPlansReturn = {
  plans: TieredPlan[];
  planMap: PlanMap;
  getBySKU: (sku?: string | null) => TieredPlan | undefined;
  findPreviousPlan: (plan: TieredPlan) => TieredPlan | null;
  loading: boolean;
};

/**
 * Source of truth for acting on the list of currently available TieredPlans in react components.
 */
export const useTieredPlans = (params?: UseTieredPlansParams): UseTieredPlansReturn => {
  const { currentPlan, unfiltered, planFilter, minimumPlanLevel } = params ?? {};
  const { data, loading } = useUseTieredPlansDataQuery();

  const filterPlans = (plans: TieredPlan[]) => {
    const minimumPlan = plans.find(
      (plan) => minimumPlanLevel != null && plan.level === minimumPlanLevel,
    );
    return unfiltered
      ? plans
      : plans
          .filter(planFilter ?? getDefaultPlansFilter(currentPlan))
          .filter((plan) => minimumPlan == null || plan.level >= minimumPlan.level);
  };

  if (isNil(data) || loading) {
    return {
      planMap: DEFAULT_PLANS_BY_TIER_MAP,
      plans: filterPlans(DEFAULT_PLANS_BY_TIER),
      getBySKU: getStaticPlanBySKU,
      findPreviousPlan: (plan: TieredPlan) => findPreviousPlan(DEFAULT_PLANS_BY_TIER_MAP, plan),
      loading,
    };
  }

  const dynamicPlans = buildDynamicPlans(data.plans);
  const dynamicPlanMap = { ...DEFAULT_PLANS_BY_TIER_MAP };
  const pricingEnrollment = (data.currentAccount?.experimentEnrollments ?? []).filter(
    (enrollment) => PRICING_EXPERIMENT_KEYS.includes(enrollment.key),
  )[0];
  const pricingExperiment = PRICING_EXPERIMENTS.find(
    (experiment) => isNotNil(pricingEnrollment) && experiment.key === pricingEnrollment.key,
  );
  const updatedDefaults = new Set<TieredPlan>();

  DEFAULT_PLANS_BY_TIER.forEach((defaultPlan) => {
    const experimentPlan = dynamicPlans.find(
      (plan) =>
        isNotNil(plan.sku) &&
        isNotNil(pricingExperiment) &&
        isNotNil(pricingEnrollment.group) &&
        pricingExperiment.match({ sku: plan.sku, group: pricingEnrollment.group }) &&
        plan.level === defaultPlan.level,
    );

    if (experimentPlan) {
      dynamicPlanMap[defaultPlan.name] = experimentPlan;
      updatedDefaults.add(experimentPlan);
    } else if (isNil(defaultPlan.isEnabled) || defaultPlan.isEnabled(pricingEnrollment)) {
      updatedDefaults.add(defaultPlan);
    } else {
      // Keep in planMap, but don't add to plans.
    }
  });

  return {
    planMap: dynamicPlanMap,
    plans: filterPlans(Array.from(updatedDefaults)),
    getBySKU: (sku?: string | null) => getDynamicPlanBySKU({ sku, plans: dynamicPlans }),
    findPreviousPlan: (plan: TieredPlan) => findPreviousPlan(dynamicPlanMap, plan),
    loading: false,
  };
};
